/**
 * Per-entry hours bounds. One source of truth shared by the API validator
 * (`apps/api/src/core/timesheets/validators.ts`, the write-time backstop) and
 * the web inline editors (`isValidHoursInput` in `timesheet-week.ts`, the
 * typing gate). Keeping the bound here means the client and server can never
 * disagree on what counts as a valid number of hours for a single entry.
 *
 * Hours are exclusive-positive — 0 is not a valid entry (clear the cell
 * instead) — and capped at a single calendar day. The DB column is
 * numeric(6,2), so this cap is the binding limit, well under the column's
 * range.
 */
export const MAX_HOURS_PER_ENTRY = 24;

/**
 * Inline-validation copy for the web editors, kept next to the bound so the
 * message and the number can't drift. Covers both failure modes (≤ 0 and
 * > MAX_HOURS_PER_ENTRY) in one sentence.
 */
export const HOURS_RANGE_MESSAGE = `Enter hours greater than 0 and no more than ${MAX_HOURS_PER_ENTRY}.`;

/**
 * Whether `n` is a valid number of hours for a single entry: finite, strictly
 * positive, and within the per-day cap. The web layer parses user text first
 * (rejecting non-numeric input) then defers the numeric range check to this;
 * the API mirrors the same bound via its zod schema.
 */
export function isHoursInRange(n: number): boolean {
  return Number.isFinite(n) && n > 0 && n <= MAX_HOURS_PER_ENTRY;
}

/**
 * Whether a stored `hours` value represents real logged time — i.e. a
 * positive number. Handles every shape the column takes: `null`/`undefined`
 * (nothing logged yet), the DB's stringified numeric (`"4.00"`), a raw
 * number, and the empty-placeholder values (`0`, `"0.00"`) that legacy rows
 * or a blanked "copy last week" entry can carry.
 *
 * This is the ONE definition of "this entry carries hours worth acting on",
 * shared by the submit-range partition (skip no-hours rows), the "copy last
 * week" planner (don't carry blanks forward), and the web submit counters.
 * Keeping it here stops those call sites from drifting — a `!= null`-only
 * check silently lets a `0.00` row through, which is exactly the kind of
 * mismatch this predicate exists to prevent. Note it is NOT range-capped:
 * an over-cap value still "has hours" (the per-entry cap is a separate gate).
 */
export function hasLoggedHours(hours: number | string | null | undefined): boolean {
  return hours != null && Number(hours) > 0;
}

/**
 * Cap on the SUM of all entries on a single calendar day. A day can't hold
 * more than 24 hours of work no matter how it's split across entries, so this
 * is the per-day total bound — distinct from {@link MAX_HOURS_PER_ENTRY}
 * (which limits one entry) even though the number is the same. Enforced inline
 * by the web day/week editors; the API still enforces the per-entry bound.
 */
export const MAX_HOURS_PER_DAY = 24;

/**
 * Whether adding `candidate` hours to a day that already has `otherHoursOnDay`
 * logged (the sum of the day's OTHER entries, excluding the one being edited)
 * stays within the per-day cap. Rounds the total to 2dp first so summing
 * numeric(6,2) values can't trip the cap on floating-point drift
 * (e.g. 23.99999999).
 */
export function isWithinDailyCap(otherHoursOnDay: number, candidate: number): boolean {
  return Math.round((otherHoursOnDay + candidate) * 100) / 100 <= MAX_HOURS_PER_DAY;
}

/**
 * Convert elapsed seconds → hours rounded to 2dp. A committed timer logs this
 * rounded value as the timesheet entry's hours, so it is also the source of
 * truth for whether a timer has anything to commit. Single definition shared by
 * the API commit guard (`apps/api/src/core/timers/service.ts`) and the web
 * timer card so the two can't disagree on the cutoff.
 */
export function secondsToHours(seconds: number): number {
  return Math.round((seconds / 3600) * 100) / 100;
}

/**
 * Whether a timer's elapsed seconds round to a positive number of hours — i.e.
 * there is actually something to commit. Below ~18s the 2dp rounding yields
 * 0.00h, which the API rejects ("Nothing to commit — timer has no elapsed
 * time."). The web card uses this to disable the commit button rather than let
 * the user click into that error.
 */
export function isCommittableElapsed(seconds: number): boolean {
  return secondsToHours(seconds) > 0;
}
